import React, { useCallback, useEffect, useRef, useState } from "react";
import "./pullToRefresh.css";

/**
 * Drag the content down from its top edge, let go, and it reloads — the gesture instead of a button.
 *
 * It arms only when the surrounding scroller is already at its top, so it never competes with ordinary
 * scrolling, and only past a slop distance, so it never turns a click on a row or a tile into a pull.
 * A pull that does cross the threshold swallows the click that the browser sends after it, which is what
 * stops a drag that started on a link from also navigating.
 *
 * `onRefresh` may return a promise; the spinner runs until it settles (and for a short minimum, so a fast
 * reload reads as a reload rather than a flicker). Failure is the caller's to report — this only spins.
 *
 * Pointer events cover mouse and touch alike. On touch the browser's own overscroll may take the gesture
 * first depending on the platform; the mouse drag is the one this app is used with.
 */

const SLOP = 6;          // travel before a drag counts as a pull rather than a click
const RESISTANCE = 0.55; // the content trails the cursor, so the pull feels weighted
const THRESHOLD = 58;    // pull distance that arms the release
const MAX = 96;
const HOLD = 34;         // where the indicator rests while the reload runs
const MIN_SPIN = 450;

const ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

/** The nearest ancestor that actually scrolls — what decides whether a downward drag is a pull. */
function findScroller(el) {
  let node = el ? el.parentElement : null;
  while (node) {
    const style = window.getComputedStyle(node);
    const oy = style.overflowY;
    if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
}

export default function PullToRefresh({ onRefresh, disabled = false, label, busyLabel, children }) {
  const rootRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, pulling: false });
  const pullRef = useRef(0);
  const aliveRef = useRef(true);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const [pull, setPullState] = useState(0);
  const [busy, setBusy] = useState(false);
  const [settling, setSettling] = useState(false);

  // Re-armed on every mount, not only the first: React's development double-mount runs this cleanup once
  // before the component is really alive, and a flag left false there would silence every reload's
  // completion — the spinner would stand for good.
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const setPull = useCallback((v) => { pullRef.current = v; setPullState(v); }, []);

  const fire = useCallback(() => {
    setBusy(true);
    setSettling(true);
    setPull(HOLD);
    const wait = new Promise((res) => setTimeout(res, MIN_SPIN));
    let call;
    try {
      call = Promise.resolve(onRefreshRef.current ? onRefreshRef.current() : null);
    } catch {
      call = Promise.resolve(null);
    }
    Promise.all([call.catch(() => null), wait]).then(() => {
      if (!aliveRef.current) return;
      setBusy(false);
      setPull(0);
    });
  }, [setPull]);

  const handlePointerDown = useCallback((e) => {
    if (disabled || busy) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const scroller = findScroller(rootRef.current);
    if ((scroller ? scroller.scrollTop : 0) > 0) return;
    dragRef.current = { active: true, startY: e.clientY, pulling: false };
  }, [disabled, busy]);

  useEffect(() => {
    const move = (e) => {
      const drag = dragRef.current;
      if (!drag.active) return;
      const dy = e.clientY - drag.startY;
      if (dy <= 0) {
        if (drag.pulling) setPull(0);
        return;
      }
      if (!drag.pulling && dy < SLOP) return;
      if (!drag.pulling) {
        drag.pulling = true;
        setSettling(false);
        // The browser has by now started selecting the text the drag passed over.
        document.body.classList.add("ptr-dragging");
        const sel = window.getSelection && window.getSelection();
        if (sel && sel.removeAllRanges) sel.removeAllRanges();
      }
      setPull(Math.min(MAX, dy * RESISTANCE));
    };

    const end = () => {
      const drag = dragRef.current;
      if (!drag.active) return;
      const pulled = drag.pulling;
      dragRef.current = { active: false, startY: 0, pulling: false };
      document.body.classList.remove("ptr-dragging");
      if (!pulled) return;

      // A pull is not a click, however it ends: swallow the one the browser is about to send, or the
      // drag that began on a counter tile would also follow its link.
      const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
      window.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 0);

      if (pullRef.current >= THRESHOLD) {
        fire();
      } else {
        setSettling(true);
        setPull(0);
      }
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [fire, setPull]);

  const progress = Math.min(1, pull / THRESHOLD);
  const ready = !busy && progress >= 1;

  return (
    <div className="ptr" ref={rootRef} onPointerDown={handlePointerDown}>
      <div
        className={`ptr-track${settling ? " ptr-track--settling" : ""}`}
        style={{ height: pull }}
        aria-hidden={pull === 0}
      >
        <span
          className={`ptr-icon${busy ? " ptr-icon--busy" : ""}${ready ? " ptr-icon--ready" : ""}`}
          style={busy ? undefined : { transform: `rotate(${Math.round(progress * 300)}deg)`, opacity: progress }}
          title={busy ? busyLabel || label : label}
        >
          {ICON}
        </span>
      </div>
      <div className="ptr-body">{children}</div>
    </div>
  );
}
